import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Menu } from "react-aria-components";
import { ChevronsUpDown, MoreVertical, MoveDown, MoveUp, Pencil, Plus, Trash2, type LucideIcon } from "lucide-react";
import type { ManualBodySection } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { useManualBodyMutations } from "../useManualMutations.js";
import { placeBefore } from "./outline-order.js";
import type { BodyWriteGate } from "./useBodyWriteGate.js";

/**
 * The orange hover line that inserts in front of whatever follows it, in the outline and between the step cards. Also
 * reachable by Tab, where it shows on focus. `className` places it, `buttonClassName` sets the height that reacts to the pointer.
 *
 * A touch screen has no hover. There the line would be an invisible tap target, so it is gone, unless `showOnTouch` turns it
 * into a small permanent plus (between the cards, where there is room). Otherwise the row's menu is the way in on touch.
 */
export function InsertSlot({
  label,
  hidden = false,
  onPress,
  className,
  buttonClassName = "h-4",
  showOnTouch = false,
  text,
  hint = false,
  lineOnly = false,
}: {
  label: string;
  hidden?: boolean;
  onPress: () => void;
  className?: string;
  buttonClassName?: string;
  showOnTouch?: boolean;
  /** Said on the line itself, where a step line and a section line can meet. */
  text?: string;
  /** Keep a faint plus in the gutter, so the line can be found: at the step list, where the room is (owner 2026-11-02). */
  hint?: boolean;
  /** Just the line, no plus (between the editor's steps — owner 2026-11-04). */
  lineOnly?: boolean;
}) {
  return (
    <div className={cn("relative h-1.5", className)}>
      <button
        type="button"
        aria-label={label}
        onClick={onPress}
        className={cn(
          "group/slot focus-visible:ring-primary absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 items-center gap-1 rounded-full border-0 bg-transparent p-0 transition-opacity outline-none focus-visible:ring-1",
          !hint && "opacity-0 hover:opacity-100 focus-visible:opacity-100",
          buttonClassName,
          hidden ? "pointer-events-none" : "cursor-pointer",
          showOnTouch ? "pointer-coarse:right-auto pointer-coarse:opacity-100" : "pointer-coarse:hidden",
        )}
      >
        {lineOnly ? null : <span
          className={cn(
            "bg-orange text-on-orange flex size-4 shrink-0 items-center justify-center rounded-full transition-[opacity,transform,background-color,color] duration-150 ease-out motion-reduce:transition-none",
            // At rest a quiet grey plus that still marks the line; orange only where the pointer or focus is (a column of
            // orange dots read as warnings in a long manual — design pass 2026-11-04).
            hint && "bg-border text-muted-foreground scale-90 opacity-70 group-hover/slot:bg-orange group-hover/slot:text-on-orange group-hover/slot:scale-110 group-hover/slot:opacity-100 group-focus-visible/slot:bg-orange group-focus-visible/slot:text-on-orange group-focus-visible/slot:opacity-100",
            showOnTouch && "pointer-coarse:size-6 pointer-coarse:opacity-100",
          )}
        >
          <Plus aria-hidden="true" className="size-3" />
        </span>}
        {text ? <span className={cn("text-orange text-xs font-semibold whitespace-nowrap pointer-coarse:hidden", hint && "opacity-0 group-hover/slot:opacity-100 group-focus-visible/slot:opacity-100")}>{text}</span> : null}
        <span
          className={cn(
            "bg-orange h-0.5 flex-1 origin-left rounded-full pointer-coarse:hidden",
            hint && "scale-x-0 opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover/slot:scale-x-100 group-hover/slot:opacity-100 group-focus-visible/slot:scale-x-100 group-focus-visible/slot:opacity-100 motion-reduce:transition-none",
          )}
        />
      </button>
    </div>
  );
}

/**
 * An inline create/rename field — focuses on mount, commits on Enter/blur, cancels on Escape. Escape stops here: it would
 * otherwise reach the editor's dialog and close the whole editor. The delayed focus is for a field opened from a row's menu,
 * which takes the focus back to its button while it closes.
 */
export function RenameInput({
  initial,
  placeholder,
  onCommit,
  onCancel,
}: {
  initial: string;
  placeholder?: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => field.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <Input
      ref={field}
      autoFocus
      aria-label={placeholder ?? t("common.name")}
      placeholder={placeholder}
      value={value}
      maxLength={255}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onCommit(value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onCommit(value);
        }
        if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        }
      }}
      className="h-7 flex-1 text-sm"
    />
  );
}

type Body = { manual: { version: number }; sections: ManualBodySection[] };
type Mutation<TVars> = { mutate: (vars: TVars, opts: { onSuccess: (data: Body) => void; onError: (err: unknown) => void }) => void };
const call = <TVars,>(mutation: Mutation<TVars>, vars: TVars) =>
  new Promise<Body>((resolve, reject) => mutation.mutate(vars, { onSuccess: resolve, onError: reject }));

/**
 * Creates a step, in front of `beforeId` or at the end. An insert is create + reorder because the create endpoint only
 * appends. Both run as ONE slot of the write queue, so a second quick insert cannot land between them and make the reorder's
 * id list stale. If the reorder fails, the new step simply stays at the end, the editor's conflict banner says why, and the
 * create's version still counts.
 */
export function createStepAt(
  gate: BodyWriteGate,
  m: Pick<ReturnType<typeof useManualBodyMutations>, "createStep" | "reorderSteps">,
  { sectionId, name, beforeId }: { sectionId: number; name: string; beforeId?: number },
): Promise<number | undefined> {
  let createdId: number | undefined;
  return gate
    .run<Body>(async (expectedVersion) => {
      const body = await call(m.createStep, { expectedVersion, sectionId, name });
      const steps = body.sections.find((s) => s.id === sectionId)?.steps ?? [];
      createdId = steps.at(-1)?.id;
      if (beforeId === undefined) return body;
      // Appended, and this queue slot is the only writer, so the new step is the last one. Not found by name: the server
      // normalises what was typed.
      const created = steps.at(-1);
      if (!created) return body;
      const order = placeBefore(steps.map((st) => st.id), created.id, beforeId);
      return call(m.reorderSteps, { expectedVersion: body.manual.version, sectionId, order }).catch(() => body);
    })
    .then(
      () => createdId,
      () => undefined,
    );
}

/** The same for a section: create + reorder as one slot of the write queue. */
export function createSectionAt(
  gate: BodyWriteGate,
  m: Pick<ReturnType<typeof useManualBodyMutations>, "createSection" | "reorderSections">,
  { name, beforeId }: { name: string; beforeId?: number },
) {
  void gate
    .run<Body>(async (expectedVersion) => {
      const body = await call(m.createSection, { expectedVersion, name });
      const created = body.sections.at(-1);
      if (beforeId === undefined || !created) return body;
      const order = placeBefore(body.sections.map((sec) => sec.id), created.id, beforeId);
      return call(m.reorderSections, { expectedVersion: body.manual.version, order }).catch(() => body);
    })
    .catch(() => {});
}

/**
 * A new manual's first section and first step (owner 2026-11-04: it starts ready to type into). ONE gate slot, so no other
 * write lands between the two and the step's CAS is the version the section's write returned. Resolves the step's id.
 * With `sectionId` the section exists already (a reload cut the first attempt short) and only the step is added.
 */
export function seedFirstStep(
  gate: BodyWriteGate,
  m: Pick<ReturnType<typeof useManualBodyMutations>, "createSection" | "createStep">,
  { sectionName, stepName, sectionId }: { sectionName: string; stepName: string; sectionId?: number },
): Promise<number | undefined> {
  let stepId: number | undefined;
  return gate
    .run<Body>(async (expectedVersion) => {
      let version = expectedVersion;
      let targetId = sectionId;
      if (targetId === undefined) {
        const withSection = await call(m.createSection, { expectedVersion, name: sectionName });
        targetId = withSection.sections.at(-1)?.id;
        if (targetId === undefined) return withSection;
        version = withSection.manual.version;
      }
      const withStep = await call(m.createStep, { expectedVersion: version, sectionId: targetId, name: stepName });
      stepId = withStep.sections.find((sec) => sec.id === targetId)?.steps.at(-1)?.id;
      return withStep;
    })
    .then(
      () => stepId,
      () => undefined,
    );
}

export type RowItem =
  | { separator: true }
  | { id: string; label: string; icon: LucideIcon; isDisabled?: boolean; variant?: "default" | "destructive"; separator?: false };

/** A per-row actions kebab (Popover + Menu) — the accessible reorder/move/rename/delete path shared by section + step rows. */
export function RowMenu({ label, items, onAction }: { label: string; items: RowItem[]; onAction: (key: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const disabledKeys = items.filter((i): i is Extract<RowItem, { id: string }> => !i.separator && !!i.isDisabled).map((i) => i.id);
  return (
    <PopoverTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="icon-sm" aria-label={t("manualEditor.actionsForLabel", { label: label })} className="shrink-0">
        <MoreVertical className="text-muted-foreground size-4" />
      </Button>
      <Popover className="w-52 gap-0 p-0" placement="bottom end">
        <Dialog aria-label={t("manualEditor.actionsForLabel", { label: label })} className="outline-none">
          <Menu
            aria-label={t("manualEditor.labelActions", { label: label })}
            className="flex flex-col p-1 outline-none"
            disabledKeys={disabledKeys}
            onAction={(key) => {
              setOpen(false);
              onAction(String(key));
            }}
          >
            {items.map((it, i) =>
              it.separator ? (
                <DropdownMenuSeparator key={`sep-${i}`} />
              ) : (
                <DropdownMenuItem key={it.id} id={it.id} textValue={it.label} variant={it.variant}>
                  <it.icon />
                  {it.label}
                </DropdownMenuItem>
              ),
            )}
          </Menu>
        </Dialog>
      </Popover>
    </PopoverTrigger>
  );
}

/**
 * A step's actions: rename, move up / down / to another section, delete. The same menu in the outline and on the step card;
 * `onRename` is what differs, because each has its own name field.
 */
export function StepActionsMenu({
  step,
  section,
  sections,
  gate,
  m,
  onRename,
  onInsert,
}: {
  step: { id: number; name: string };
  section: ManualBodySection;
  sections: ManualBodySection[];
  gate: BodyWriteGate;
  m: Pick<ReturnType<typeof useManualBodyMutations>, "reorderSteps" | "moveStep" | "deleteStep">;
  onRename: () => void;
  /** Start naming a new step in front of `beforeId`, or at the section's end when it is `undefined`. Absent: no insert entries. */
  onInsert?: (beforeId: number | undefined) => void;
}) {
  const { t } = useTranslation();
  const { enqueue } = gate;
  const ids = section.steps.map((s) => s.id);
  const index = ids.indexOf(step.id);
  const moveInSection = (dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    const order = [...ids];
    [order[index], order[j]] = [order[j]!, order[index]!];
    void enqueue(m.reorderSteps, (expectedVersion) => ({ expectedVersion, sectionId: section.id, order })).catch(() => {});
  };
  const moveToSection = (toSectionId: number) => {
    const target = sections.find((s) => s.id === toSectionId);
    if (!target) return;
    const order = [...target.steps.map((s) => s.id), step.id];
    void enqueue(m.moveStep, (expectedVersion) => ({ expectedVersion, stepId: step.id, toSectionId, order })).catch(() => {});
  };
  return (
    <RowMenu
      label={t("manualEditor.stepAria", { name: step.name })}
      items={[
        { id: "rename", label: t("manualEditor.rename"), icon: Pencil },
        ...(onInsert
          ? ([
              { id: "insertBefore", label: t("manualEditor.insertStepAbove"), icon: Plus },
              { id: "insertAfter", label: t("manualEditor.insertStepBelow"), icon: Plus },
            ] as RowItem[])
          : []),
        { id: "up", label: t("manualEditor.moveUp"), icon: MoveUp, isDisabled: index === 0 },
        { id: "down", label: t("manualEditor.moveDown"), icon: MoveDown, isDisabled: index === ids.length - 1 },
        ...(sections.length > 1
          ? ([{ separator: true }] as RowItem[]).concat(
              sections
                .filter((s) => s.id !== section.id)
                .map((s) => ({ id: `move:${s.id}`, label: t("manualEditor.moveToSection", { name: s.name }), icon: ChevronsUpDown })),
            )
          : []),
        { separator: true },
        { id: "delete", label: t("manualEditor.deleteStep"), icon: Trash2, variant: "destructive" },
      ]}
      onAction={(key) => {
        if (key === "rename") onRename();
        else if (key === "insertBefore") onInsert?.(step.id);
        else if (key === "insertAfter") onInsert?.(ids[index + 1]);
        else if (key === "up") moveInSection(-1);
        else if (key === "down") moveInSection(1);
        else if (key === "delete") void enqueue(m.deleteStep, (expectedVersion) => ({ expectedVersion, stepId: step.id })).catch(() => {});
        else if (key.startsWith("move:")) moveToSection(Number(key.slice(5)));
      }}
    />
  );
}

/** A section's actions, the same menu on its row in the outline and on its heading in the step list. */
export function SectionActionsMenu({
  section,
  sections,
  gate,
  m,
  onRename,
  onInsert,
}: {
  section: { id: number; name: string };
  sections: ManualBodySection[];
  gate: BodyWriteGate;
  m: Pick<ReturnType<typeof useManualBodyMutations>, "reorderSections" | "deleteSection">;
  onRename: () => void;
  /** Start naming a new section in front of `beforeId`, or at the end when it is `undefined`. Absent: no insert entries. */
  onInsert?: (beforeId: number | undefined) => void;
}) {
  const { t } = useTranslation();
  const { enqueue } = gate;
  const ids = sections.map((sec) => sec.id);
  const index = ids.indexOf(section.id);
  const move = (dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    const order = [...ids];
    [order[index], order[j]] = [order[j]!, order[index]!];
    void enqueue(m.reorderSections, (expectedVersion) => ({ expectedVersion, order })).catch(() => {});
  };
  return (
    <RowMenu
      label={t("manualEditor.sectionAria", { name: section.name })}
      items={[
        { id: "rename", label: t("manualEditor.rename"), icon: Pencil },
        ...(onInsert
          ? ([
              { id: "insertBefore", label: t("manualEditor.insertSectionAbove"), icon: Plus },
              { id: "insertAfter", label: t("manualEditor.insertSectionBelow"), icon: Plus },
            ] as RowItem[])
          : []),
        { id: "up", label: t("manualEditor.moveUp"), icon: MoveUp, isDisabled: index === 0 },
        { id: "down", label: t("manualEditor.moveDown"), icon: MoveDown, isDisabled: index === ids.length - 1 },
        { separator: true },
        { id: "delete", label: t("manualEditor.deleteSection"), icon: Trash2, variant: "destructive", isDisabled: ids.length <= 1 },
      ]}
      onAction={(key) => {
        if (key === "rename") onRename();
        else if (key === "insertBefore") onInsert?.(section.id);
        else if (key === "insertAfter") onInsert?.(ids[index + 1]);
        else if (key === "up") move(-1);
        else if (key === "down") move(1);
        else if (key === "delete") void enqueue(m.deleteSection, (expectedVersion) => ({ expectedVersion, sectionId: section.id })).catch(() => {});
      }}
    />
  );
}
